import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { getUser } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

export default function AdminContractTemplate() {
  const nav = useNavigate();
  const user = getUser();
  const isDiretor = user?.role === "diretor";
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/contracts/template").then((r) => setTpl(r.data));
  }, []);

  async function save() {
    if (!tpl) return;
    setSaving(true);
    try {
      await api.put("/admin/contracts/template", {
        intro_html: tpl.intro_html,
        clauses: tpl.clauses,
        closing_html: tpl.closing_html,
      });
      toast.success("Modelo salvo");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  }

  if (!tpl) return <div className="text-sm text-slate-500">Carregando…</div>;

  const updateClause = (i, k, v) => {
    const list = [...tpl.clauses];
    list[i] = { ...list[i], [k]: v };
    setTpl({ ...tpl, clauses: list });
  };

  return (
    <div className="max-w-4xl" data-testid="admin-contract-template-page">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <button onClick={() => nav("/admin/contratos")} className="text-xs text-slate-500 hover:text-[#0AA89C] inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Voltar aos contratos
          </button>
          <h1 className="text-3xl font-semibold text-[#0AA89C]">Modelo padrão do contrato</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Texto base das 15 cláusulas. Variáveis entre <code>{`{{}}`}</code> são substituídas automaticamente em cada contrato.
            A Cláusula 10ª usa <code>{`{{CLAUSULA_10_PAGAMENTO_CANCELAMENTO}}`}</code> — o texto é gerado a partir das condições de pagamento definidas na reserva (multa fixa de 30%).
          </p>
        </div>
        {isDiretor && (
          <Button onClick={save} disabled={saving} className="bg-[#0AA89C] hover:bg-[#066B63]" data-testid="save-template">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Salvando…" : "Salvar modelo"}
          </Button>
        )}
      </div>

      {!isDiretor && (
        <div className="mb-6 p-4 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-900">
          Apenas o Diretor pode editar o modelo. Você está em modo consulta.
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-md p-6 mb-6">
        <Label className="text-sm font-semibold text-[#22302E]">Introdução (partes)</Label>
        <Textarea rows={6} value={tpl.intro_html} disabled={!isDiretor} onChange={(e) => setTpl({ ...tpl, intro_html: e.target.value })} className="mt-2 font-mono text-sm" data-testid="template-intro" />
      </section>

      <div className="space-y-4">
        {tpl.clauses.map((cl, i) => (
          <section key={i} className="bg-white border border-slate-200 rounded-md p-6">
            <div className="grid grid-cols-6 gap-3 mb-3">
              <div className="col-span-1">
                <Label>Número</Label>
                <Input type="number" value={cl.number} disabled={!isDiretor} onChange={(e) => updateClause(i, "number", Number(e.target.value))} />
              </div>
              <div className="col-span-5">
                <Label>Título</Label>
                <Input value={cl.title} disabled={!isDiretor} onChange={(e) => updateClause(i, "title", e.target.value)} />
              </div>
            </div>
            <Label>Corpo</Label>
            <Textarea rows={cl.number === 10 ? 3 : 5} value={cl.body} disabled={!isDiretor} onChange={(e) => updateClause(i, "body", e.target.value)} className="mt-1 font-mono text-sm" data-testid={`template-clause-${cl.number}`} />
            {cl.number === 10 && (
              <p className="text-xs text-slate-500 mt-2">
                Nesta cláusula o texto exato é gerado automaticamente por reserva. Não altere o placeholder <code>{`{{CLAUSULA_10_PAGAMENTO_CANCELAMENTO}}`}</code>.
              </p>
            )}
          </section>
        ))}
      </div>

      <section className="bg-white border border-slate-200 rounded-md p-6 mt-6">
        <Label className="text-sm font-semibold text-[#22302E]">Encerramento</Label>
        <Textarea rows={3} value={tpl.closing_html} disabled={!isDiretor} onChange={(e) => setTpl({ ...tpl, closing_html: e.target.value })} className="mt-2 font-mono text-sm" data-testid="template-closing" />
      </section>

      {isDiretor && (
        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[#0AA89C] hover:bg-[#066B63]"><Save className="w-4 h-4 mr-1" /> {saving ? "Salvando…" : "Salvar modelo"}</Button>
        </div>
      )}
    </div>
  );
}
